import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Col,
  Row,
  Spinner,
  Table,
  Form,
  Button,
  Badge,
} from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiActivity, FiArrowLeft, FiRefreshCw } from "react-icons/fi";

const getToken = () => localStorage.getItem("jwt");

const KINDS = [
  { key: "cat", label: "Categories" },
  { key: "search", label: "Search" },
  { key: "pid", label: "PIDs" },
  { key: "wh", label: "Webhooks" },
];

const GROUP_BY = [
  { key: "none", label: "None" },
  { key: "bucket_key", label: "Category / Keyword" },
  { key: "page", label: "Page" },
  { key: "shard", label: "Shard" },
];

const REFRESH_MS = 30000;

function fmtBucketLabel(ts) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function aggregateByKey(rollups, kind) {
  // Group rollups by bucket_key, summing counters across minutes.
  const out = new Map();
  for (const r of rollups) {
    const key = r.bucket_key || "(none)";
    if (!out.has(key)) {
      out.set(key, {
        bucket_key: key,
        count: 0,
        sum_new: 0,
        sum_notify_count: 0,
        url_drift: 0,
        err_count: 0,
        restock_missed: 0,
        last_seen: r.ts,
      });
    }
    const agg = out.get(key);
    agg.count += r.count || 0;
    agg.sum_new += r.sum_new || 0;
    agg.sum_notify_count += r.sum_notify_count || 0;
    agg.url_drift += r.url_drift || 0;
    agg.err_count += r.err_count || 0;
    agg.restock_missed += r.restock_missed || 0;
    if (new Date(r.ts) > new Date(agg.last_seen)) agg.last_seen = r.ts;
  }
  return Array.from(out.values()).sort((a, b) => b.count - a.count);
}

function buildTimeline(rollups, kind) {
  // Aggregate per-minute bucket across bucket_keys for chart display.
  const out = new Map();
  for (const r of rollups) {
    const tsKey = new Date(r.ts).toISOString();
    if (!out.has(tsKey)) {
      out.set(tsKey, {
        ts: tsKey,
        label: fmtBucketLabel(r.ts),
        count: 0,
        notify_count: 0,
        new_pids: 0,
        url_drift: 0,
        errors: 0,
        restock_missed: 0,
      });
    }
    const t = out.get(tsKey);
    t.count += r.count || 0;
    t.notify_count += r.sum_notify_count || 0;
    t.new_pids += r.sum_new || 0;
    t.url_drift += r.url_drift || 0;
    t.errors += r.err_count || 0;
    t.restock_missed += r.restock_missed || 0;
  }
  return Array.from(out.values()).sort((a, b) => new Date(a.ts) - new Date(b.ts));
}

const AdminObservability = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { toggleSidebar } = useOutletContext() || {};

  const [kind, setKind] = useState("cat");
  const [hours, setHours] = useState(24);
  const [bucketFilter, setBucketFilter] = useState("");
  const [groupBy, setGroupBy] = useState("bucket_key");
  const [rollups, setRollups] = useState([]);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ categories: {}, keywords: {} });
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchAll = useCallback(async () => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    const params = new URLSearchParams({ kind, hours: String(hours) });
    if (bucketFilter) params.set("bucket_key", bucketFilter);

    try {
      const [rRes, eRes, pRes] = await Promise.all([
        fetch(`${getHost()}/api/admin/observability/${encodeURIComponent(code)}/rollups?${params.toString()}`, { headers }),
        fetch(`${getHost()}/api/admin/observability/${encodeURIComponent(code)}/stream?kind=${kind}&limit=200`, { headers }),
        fetch(`${getHost()}/api/admin/observability/${encodeURIComponent(code)}/pagination-state`, { headers }),
      ]);
      if (rRes.ok) setRollups((await rRes.json()).rollups || []);
      if (eRes.ok) setEvents((await eRes.json()).events || []);
      if (pRes.ok) setPagination(await pRes.json());
    } catch {
      // Keep existing state
    } finally {
      setLoading(false);
    }
  }, [code, kind, hours, bucketFilter]);

  useEffect(() => {
    setLoading(true);
    fetchAll();
    const id = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  const timeline = useMemo(() => buildTimeline(rollups, kind), [rollups, kind]);
  const byKey = useMemo(() => aggregateByKey(rollups, kind), [rollups, kind]);

  const groupedEvents = useMemo(() => {
    if (groupBy === "none") return null;
    const groups = new Map();
    for (const e of events) {
      let key;
      if (groupBy === "bucket_key") key = e.bucket_key || "(none)";
      else if (groupBy === "page") key = e.fields?.page || "(none)";
      else if (groupBy === "shard") key = e.fields?.shard || "(none)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [events, groupBy]);

  const paginationRows = useMemo(() => {
    const isCategoryKind = kind === "cat";
    const map = isCategoryKind ? pagination.categories : pagination.keywords;
    return Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  }, [kind, pagination]);

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />

      <div className="pb-3 mx-4 content-body">
        <div className="d-flex align-items-center mb-2">
          <Button
            variant="link"
            className="p-0 me-2 text-white-65"
            onClick={() => navigate(-1)}
            title="Back"
          >
            <FiArrowLeft />
          </Button>
          <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 me-2">
            <FiActivity className="text-white" />
          </div>
          <div>
            <h4 className="fs-22 text-main mb-0">Observability</h4>
            <p className="text-white-65 fs-13 mb-0">{code}</p>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            className="ms-auto me-2"
            onClick={() => navigate(`/admin/logs/${encodeURIComponent(code)}`)}
            title="View logs"
          >
            Logs
          </Button>
          <Button
            variant="outline-light"
            size="sm"
            onClick={fetchAll}
            title="Refresh"
          >
            <FiRefreshCw />
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 mb-3">
        <Card className="border border-dark bg-transparent">
          <Card.Body className="p-3">
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">Kind</Form.Label>
                <Form.Select
                  size="sm"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value);
                    setBucketFilter("");
                  }}
                >
                  {KINDS.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">Time range (hours)</Form.Label>
                <Form.Select
                  size="sm"
                  value={hours}
                  onChange={(e) => setHours(Number(e.target.value))}
                >
                  <option value={1}>Last 1h</option>
                  <option value={6}>Last 6h</option>
                  <option value={24}>Last 24h</option>
                  <option value={72}>Last 3d</option>
                  <option value={168}>Last 7d</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">
                  Filter bucket_key
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="e.g. spielen/pokemon"
                  value={bucketFilter}
                  onChange={(e) => setBucketFilter(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="fs-12 text-white-65 mb-1">
                  Recent events — group by
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  {GROUP_BY.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* Timeline */}
          <div className="px-4 mb-3">
            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-2">
                  <h6 className="text-main fs-14 mb-0">Timeline (per minute)</h6>
                  <span className="text-white-65 fs-12">{timeline.length} buckets</span>
                </div>
                {timeline.length === 0 ? (
                  <p className="text-white-65 fs-13 mb-0">No data in this window.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" stroke="#888" fontSize={11} />
                      <YAxis stroke="#888" fontSize={11} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
                        labelStyle={{ color: "#fff" }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" stackId="a" fill="#7C98FF" name="calls" />
                      <Bar dataKey="notify_count" stackId="b" fill="#00E98E" name="notify_count" />
                      <Bar dataKey="errors" stackId="c" fill="#FF4444" name="errors" />
                      <Bar dataKey="url_drift" stackId="c" fill="#FF9F1C" name="url_drift" />
                      {kind === "pid" && (
                        <Bar dataKey="restock_missed" stackId="c" fill="#FFC107" name="restock_missed" />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card.Body>
            </Card>
          </div>

          <Row className="mx-0 px-3 g-3 mb-3">
            {/* By-bucket-key table */}
            <Col lg={kind === "cat" || kind === "search" ? 8 : 12}>
              <Card className="border border-dark bg-transparent h-100">
                <Card.Body className="p-3">
                  <h6 className="text-main fs-14 mb-2">
                    By {kind === "wh" ? "webhook" : kind === "pid" ? "(monitor-level)" : "bucket"}
                  </h6>
                  {byKey.length === 0 ? (
                    <p className="text-white-65 fs-13 mb-0">No buckets in window.</p>
                  ) : (
                    <div className="table-mobile-scroll">
                      <Table className="table custom-table table-bordered" hover size="sm">
                        <thead>
                          <tr>
                            <th className="fs-12 fw-500 border-bottom">Bucket</th>
                            <th className="fs-12 fw-500 border-bottom">Calls</th>
                            {kind === "pid" ? (
                              <th className="fs-12 fw-500 border-bottom">Restock missed</th>
                            ) : (
                              <th className="fs-12 fw-500 border-bottom">Notify count</th>
                            )}
                            <th className="fs-12 fw-500 border-bottom">URL drift</th>
                            <th className="fs-12 fw-500 border-bottom">Errors</th>
                            <th className="fs-12 fw-500 border-bottom">Last seen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byKey.slice(0, 100).map((row) => (
                            <tr
                              key={row.bucket_key}
                              className="cursor-pointer"
                              onClick={() =>
                                setBucketFilter(row.bucket_key === "(none)" ? "" : row.bucket_key)
                              }
                            >
                              <td className="py-1 fs-12 text-main">{row.bucket_key}</td>
                              <td className="py-1 fs-12 text-white-65">{row.count}</td>
                              <td className="py-1 fs-12 text-white-65">
                                {kind === "pid" ? row.restock_missed : row.sum_notify_count}
                              </td>
                              <td className="py-1 fs-12">
                                {row.url_drift > 0 ? (
                                  <Badge bg="" style={{ backgroundColor: "#FF9F1C22", color: "#FF9F1C" }}>
                                    {row.url_drift}
                                  </Badge>
                                ) : (
                                  <span className="text-white-65">0</span>
                                )}
                              </td>
                              <td className="py-1 fs-12">
                                {row.err_count > 0 ? (
                                  <span style={{ color: "#FF4444" }}>{row.err_count}</span>
                                ) : (
                                  <span className="text-white-65">0</span>
                                )}
                              </td>
                              <td className="py-1 fs-12 text-white-65">
                                {new Date(row.last_seen).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Current pagination panel */}
            {(kind === "cat" || kind === "search") && (
              <Col lg={4}>
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <h6 className="text-main fs-14 mb-2">Current pagination</h6>
                    <p className="text-white-65 fs-12 mb-2">
                      Live state from syncer mirror
                      {pagination.ts ? ` · synced ${new Date(pagination.ts).toLocaleTimeString()}` : ""}
                    </p>
                    {paginationRows.length === 0 ? (
                      <p className="text-white-65 fs-13 mb-0">No pagination state recorded.</p>
                    ) : (
                      <div className="table-mobile-scroll" style={{ maxHeight: 280, overflowY: "auto" }}>
                        <Table className="table custom-table table-bordered" size="sm">
                          <thead>
                            <tr>
                              <th className="fs-12 fw-500 border-bottom">
                                {kind === "cat" ? "Category" : "Keyword"}
                              </th>
                              <th className="fs-12 fw-500 border-bottom">Page</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginationRows.map(([k, page]) => (
                              <tr key={k}>
                                <td className="py-1 fs-12 text-main">{k}</td>
                                <td className="py-1 fs-12 text-white-65">{page}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {/* Recent events panel */}
          <div className="px-4 mb-4">
            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between mb-2">
                  <h6 className="text-main fs-14 mb-0">Recent events ({events.length})</h6>
                  <span className="text-white-65 fs-12">Mongo mirror — 6h TTL</span>
                </div>
                {events.length === 0 ? (
                  <p className="text-white-65 fs-13 mb-0">No recent events.</p>
                ) : groupedEvents ? (
                  groupedEvents.map(([groupKey, list]) => (
                    <div key={groupKey} className="mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <Badge bg="" className="text-wrap text-start min-w-0" style={{ backgroundColor: "#7C98FF22", color: "#7C98FF", overflowWrap: "anywhere" }}>
                          {groupKey}
                        </Badge>
                        <span className="text-white-65 fs-12 ms-2">{list.length} events</span>
                      </div>
                      <EventTable
                        events={list.slice(0, 20)}
                        kind={kind}
                        expandedRow={expandedRow}
                        setExpandedRow={setExpandedRow}
                      />
                    </div>
                  ))
                ) : (
                  <EventTable
                    events={events}
                    kind={kind}
                    expandedRow={expandedRow}
                    setExpandedRow={setExpandedRow}
                  />
                )}
              </Card.Body>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

function EventTable({ events, kind, expandedRow, setExpandedRow }) {
  return (
    <div className="table-mobile-scroll">
      <Table className="table custom-table table-bordered" hover size="sm">
        <thead>
          <tr>
            <th className="fs-12 fw-500 border-bottom" style={{ width: 140 }}>Timestamp</th>
            <th className="fs-12 fw-500 border-bottom">Bucket</th>
            <th className="fs-12 fw-500 border-bottom" style={{ width: 70 }}>Shard</th>
            <th className="fs-12 fw-500 border-bottom" style={{ width: 60 }}>Page</th>
            <th className="fs-12 fw-500 border-bottom" style={{ width: 70 }}>Status</th>
            <th className="fs-12 fw-500 border-bottom">Signals</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const f = e.fields || {};
            const signals = [];
            if (f.miss === "True") signals.push(["restock_missed", "#FFC107"]);
            if (f.drift === "True") signals.push(["url_drift", "#FF9F1C"]);
            if (f.err) signals.push([f.err, "#FF4444"]);
            const isOpen = expandedRow === e._id;
            return (
              <React.Fragment key={e._id}>
                <tr
                  className="cursor-pointer"
                  onClick={() => setExpandedRow(isOpen ? null : e._id)}
                >
                  <td className="py-1 fs-12 text-white-65">
                    {new Date(e.ts).toLocaleTimeString()}
                  </td>
                  <td className="py-1 fs-12 text-main">{e.bucket_key || "—"}</td>
                  <td className="py-1 fs-12 text-white-65">{f.shard || "—"}</td>
                  <td className="py-1 fs-12 text-white-65">{f.page || "—"}</td>
                  <td className="py-1 fs-12 text-white-65">{f.status || "—"}</td>
                  <td className="py-1 fs-12">
                    {signals.length === 0 ? (
                      <span className="text-white-65">—</span>
                    ) : (
                      signals.map(([label, color], i) => (
                        <Badge
                          key={i}
                          bg=""
                          className="me-1"
                          style={{ backgroundColor: `${color}22`, color }}
                        >
                          {label}
                        </Badge>
                      ))
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={6} className="py-2" style={{ backgroundColor: "#0a0a0a" }}>
                      <div className="text-white-65 fs-12 mb-1">final_url:</div>
                      <code className="text-main fs-12 d-block mb-2" style={{ wordBreak: "break-all" }}>
                        {f.url || "—"}
                      </code>
                      <div className="text-white-65 fs-12 mb-1">body_sample:</div>
                      <pre
                        className="text-main fs-11 mb-0 p-2"
                        style={{ backgroundColor: "#1a1a1a", border: "1px solid #333", whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}
                      >
                        {f.body || "—"}
                      </pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default AdminObservability;
